import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { FirestoreProvider, FirestoreWriteBatch } from '@lemon/core-firebase/service/lasku/lasku-tallennus.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { Tilintarkastaja, TilintarkastajaKutsu, TilintarkastajanKutsuTyojono, TilintarkastajanTila, TilintarkastajaOikeus } from '../model/tilintarkastaja'
import { TilintarkastajaUriService } from '../service/tilintarkastaja-uri.service'

export const TILINTARKASTAJAN_TILA_TEKSTI: Record<TilintarkastajanTila, string> = {
  kutsuttu: 'Kutsuttu',
  tunnistautunut: 'Tunnistautunut',
  hylatty: 'Hylätty',
}

export const TILINTARKASTAJA_KUTSU_VOIMASSAOLO_PAIVINA = 10
export const TILINTARKASTAJA_OIKEUS_VOIMASSAOLO_PAATTYY = { year: 2099, month: 12, day: 31 }
export const TILINTARKASTAJA_KUTSU_TAIKALINKKI_PLACEHOLDER = '<taikalinkki tulee tähän>'
export const TILINTARKASTAJA_KUTSU_VIESTIN_AIHE = 'Kutsu tilintarkastajaksi Lemonatoriin'
export const TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_YLA = `Hei,

sinut on kutsuttu tilintarkastajaksi Lemonaidiin. Voit kirjautua ensimmäisen kerran alla olevasta linkistä pankkitunnuksillasi.`
export const TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI = `
${TILINTARKASTAJA_KUTSU_TAIKALINKKI_PLACEHOLDER}

Linkki on voimassa ${TILINTARKASTAJA_KUTSU_VOIMASSAOLO_PAIVINA} päivää.

`
export const TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_ALA = `Ystävällisin terveisin,
Tilitoimisto Lemon Tree Oy
info@lemontree.fi
010 517 6020
`

export class TilintarkastajaService {
  constructor(
    private tilintarkastajaUriService: TilintarkastajaUriService,
    private timestampService: TimestampProviderBase,
    private kopioijaPalvelu: KopioijaPalvelu,
    private firestoreProvider: FirestoreProvider,
  ) {}

  public annaUusiTilintarkastaja(avain: string): Tilintarkastaja {
    const now = this.timestampService.now()
    return {
      avain: avain,
      etunimi: '',
      sukunimi: '',
      email: '',
      hetu: null,
      tila: null,
      luotu: now,
      luoja: '',
      paivittaja: '',
      paivitetty: now,
      poistettu: false,
    }
  }

  public annaUusiTilintarkastajaKutsu(tilintarkastajaAvain: string, kutsunAvain: string): TilintarkastajaKutsu {
    const kutsu: TilintarkastajaKutsu = {
      avain: kutsunAvain,
      tilintarkastajaAvain: tilintarkastajaAvain,
      vastaanottaja: null,
      tunnistautunut: null,
      linkinAvain: null,
      lahetetty: null,
      voimassaoloPaattyy: null,
      viestinSisalto: null,
      viestinAihe: null,
      luotu: this.timestampService.now(),
      luoja: null,
      paivittaja: null,
      paivitetty: this.timestampService.now(),
    }
    return kutsu
  }

  public annaUusiTilintarkastajaOikeus(avain: string, tilintarkastajAvain: string, asiakasAvain: string, tilikausiAvain: string, kirjanpitajaAvain: string): TilintarkastajaOikeus {
    const now = this.timestampService.now()
    return {
      avain: avain,
      tilintarkastajaAvain: tilintarkastajAvain,
      asiakasAvain: asiakasAvain,
      tilikausiAvain: tilikausiAvain,
      voimassaoloPaattyy: null,
      paattymisenSyy: null,
      luoja: kirjanpitajaAvain,
      luotu: now,
      paivittaja: kirjanpitajaAvain,
      paivitetty: now,
    }
  }

  public kopioiTilintarkastaja(kopioitava: Tilintarkastaja): Tilintarkastaja {
    return this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<Tilintarkastaja>(kopioitava)
  }
  public kopioiTilintarkastajaKutsu(kopioitava: TilintarkastajaKutsu): TilintarkastajaKutsu {
    return this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<TilintarkastajaKutsu>(kopioitava)
  }
  public kopioiTilintarkastajaOikeus(kopioitava: TilintarkastajaOikeus): TilintarkastajaOikeus {
    return this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<TilintarkastajaOikeus>(kopioitava)
  }

  public annaTilintarkastajaKutsuViesti(ylaosa: string, taikalinkkiosa: string, alaosa: string): string {
    return `${ylaosa}\n\n${taikalinkkiosa}\n\n${alaosa}`
  }

  public luoJaKutsuTilintarkastajaBatchiin(perustiedot: { etunimi: string; sukunimi: string; email: string }, kutsuViesti: string, kutsuAihe: string, userId: string, batch: FirestoreWriteBatch) {
    const now = this.timestampService.now()
    const tilintarkastaja: Tilintarkastaja = this.annaUusiTilintarkastaja(this.firestoreProvider.annaUusiAvain())
    tilintarkastaja.etunimi = perustiedot.etunimi
    tilintarkastaja.sukunimi = perustiedot.sukunimi
    tilintarkastaja.email = perustiedot.email
    tilintarkastaja.tila = null
    tilintarkastaja.luoja = userId
    tilintarkastaja.luotu = now
    tilintarkastaja.paivittaja = userId
    tilintarkastaja.paivitetty = now

    const kutsu: TilintarkastajaKutsu = this.annaUusiTilintarkastajaKutsu(tilintarkastaja.avain, this.firestoreProvider.annaUusiAvain())
    kutsu.tilintarkastajaAvain = tilintarkastaja.avain
    kutsu.vastaanottaja = perustiedot.email
    kutsu.viestinSisalto = kutsuViesti
    kutsu.viestinAihe = kutsuAihe
    kutsu.luoja = userId
    kutsu.luotu = now
    kutsu.paivittaja = userId
    kutsu.paivitetty = now

    this.lisaaTilintarkastajaBatchiin(tilintarkastaja, userId, batch)
    this.lisaaTilintarkastajaKutsuBatchiin(kutsu, batch)
    this.lisaaTilintarkastajaKutsuTyojonoBatchiin(tilintarkastaja.avain, kutsu.avain, batch)
    return { tilintarkastaja, kutsu }
  }

  public lisaaTilintarkastajaBatchiin(tilintarkastaja: Tilintarkastaja, userId: string, batch: FirestoreWriteBatch, paivitaLemonaidAsiakkaat: boolean = false) {
    tilintarkastaja.paivittaja = userId
    tilintarkastaja.paivitetty = this.timestampService.now()

    const uri = this.tilintarkastajaUriService.annaTilintarkastajaUri(tilintarkastaja.avain)
    const historiaUri = this.tilintarkastajaUriService.annaTilintarkastajaHistoriaUri(tilintarkastaja.avain, this.firestoreProvider.annaUusiAvain())
    batch.set(this.firestoreProvider.annaDoc(uri), tilintarkastaja, { merge: true })
    batch.set(this.firestoreProvider.annaDoc(historiaUri), this.kopioiTilintarkastaja(tilintarkastaja))

    if (paivitaLemonaidAsiakkaat) {
      // Esim. jos tilintarkastajan tilaa muuttuu tai poistetaan (vaikuttaa Lemonaid-päässä asiakkaan tilintarkastaja-oikeuksiin)
      this.synkronoiTilintarkastajanAsiakkaatLemonaidiinBatchiin(tilintarkastaja, batch)
    }
  }

  public lisaaTilintarkastajaKutsuBatchiin(kutsu: TilintarkastajaKutsu, batch: FirestoreWriteBatch) {
    const kutsuUri = this.tilintarkastajaUriService.annaTilintarkastajaKutsuUri(kutsu.tilintarkastajaAvain, kutsu.avain)
    const kutsuHistoriaUri = this.tilintarkastajaUriService.annaTilintarkastajaKutsuHistoriaUri(kutsu.tilintarkastajaAvain, kutsu.avain, this.firestoreProvider.annaUusiAvain())
    batch.set(this.firestoreProvider.annaDoc(kutsuUri), kutsu, { merge: true })
    batch.set(this.firestoreProvider.annaDoc(kutsuHistoriaUri), this.kopioiTilintarkastajaKutsu(kutsu))
  }

  public lisaaTilintarkastajaKutsuTyojonoBatchiin(tilintarkastajaAvain: string, kutsunAvain: string, batch: FirestoreWriteBatch) {
    const tyojonoUri = this.tilintarkastajaUriService.annaTilintarkastajaKutsuTyojonoUri(tilintarkastajaAvain)
    const tyojonoData: TilintarkastajanKutsuTyojono = {
      tilintarkastajaAvain: tilintarkastajaAvain,
      kutsunAvain: kutsunAvain,
      luotu: this.timestampService.now(),
    }
    batch.set(this.firestoreProvider.annaDoc(tyojonoUri), tyojonoData)
  }

  public lisaaTilintarkastajaOikeusBatchiin(tilintarkastaja: Tilintarkastaja, oikeus: TilintarkastajaOikeus, userId: string, batch: FirestoreWriteBatch) {
    const uri = this.tilintarkastajaUriService.annaTilintarkastajaOikeusUri(oikeus.tilintarkastajaAvain, oikeus.avain)
    const historiaUri = this.tilintarkastajaUriService.annaTilintarkastajaOikeusHistoriaUri(oikeus.tilintarkastajaAvain, oikeus.avain, this.firestoreProvider.annaUusiAvain())
    batch.set(this.firestoreProvider.annaDoc(uri), oikeus, { merge: true })
    batch.set(this.firestoreProvider.annaDoc(historiaUri), this.kopioiTilintarkastajaOikeus(oikeus))

    // Päivitä tilintarkastajan päädokumenttiin oikeudet
    this.lisaaOikeusTilintarkastajaanBatchiin(tilintarkastaja, oikeus, userId, batch)

    // Synkronoi asiakkaan tiedot Lemonaidiin (jotta tilintarkastajaoikeudet päivittyvät sinnekin)
    this.synkronoiAsiakasLemonaidiinBatchiin(oikeus.asiakasAvain, batch)
  }

  public lisaaOikeusTilintarkastajaanBatchiin(tilintarkastaja: Tilintarkastaja, oikeus: TilintarkastajaOikeus, userId: string, batch: FirestoreWriteBatch) {
    const onkoVoimassa = oikeus.voimassaoloPaattyy.toMillis() > this.timestampService.now().toMillis()

    if (onkoVoimassa) {
      tilintarkastaja.asiakasOikeudet = {
        [oikeus.asiakasAvain]: {
          voimassa: oikeus.voimassaoloPaattyy,
          tilikaudet: { [oikeus.tilikausiAvain]: oikeus.voimassaoloPaattyy },
        },
      }
    } else {
      const muutTilikaudet = Object.keys(tilintarkastaja.asiakasOikeudet?.[oikeus.asiakasAvain]?.tilikaudet ?? {}).filter(k => k !== oikeus.tilikausiAvain)

      tilintarkastaja.asiakasOikeudet = {
        [oikeus.asiakasAvain]: muutTilikaudet.length > 0 ? { tilikaudet: { [oikeus.tilikausiAvain]: this.firestoreProvider.annaDeleteArvo() } } : this.firestoreProvider.annaDeleteArvo(),
      }
    }

    this.lisaaTilintarkastajaBatchiin(tilintarkastaja, userId, batch)
  }

  onkoOikeusVoimassa(tilintarkastaja: Tilintarkastaja, asiakasAvain: string): boolean {
    if (tilintarkastaja.tila !== 'tunnistautunut') {
      return false
    }

    if (tilintarkastaja.poistettu) {
      return false
    }

    // Tarkista onko oikeus asiakkaaseen
    const asiakasOikeus = tilintarkastaja.asiakasOikeudet?.[asiakasAvain]
    if (!asiakasOikeus) {
      console.warn('Tilintarkastajalla ei ole oikeuksia asiakkaaseen', asiakasAvain)
      return false
    }

    // Tarkista onko oikeus voimassa
    const now = this.timestampService.now()
    if (!asiakasOikeus.voimassa || asiakasOikeus.voimassa.toMillis() < now.toMillis()) {
      console.warn('Tilintarkastajan oikeus on vanhentunut', asiakasOikeus.voimassa, now)
      return false
    }
    return true
  }

  /**
   * Luo työjonot kaikkien tilintarkastajan asiakkaiden synkronointiin Lemonaidiin.
   * Käytetään kun tilintarkastajan tila tai poistettu-kenttä muuttuu.
   */
  public synkronoiTilintarkastajanAsiakkaatLemonaidiinBatchiin(tilintarkastaja: Tilintarkastaja, batch: FirestoreWriteBatch) {
    // Hae kaikki asiakkaat joilla on oikeudet
    if (!tilintarkastaja.asiakasOikeudet) {
      return
    }

    for (const asiakasAvain of Object.keys(tilintarkastaja.asiakasOikeudet)) {
      this.synkronoiAsiakasLemonaidiinBatchiin(asiakasAvain, batch)
    }
  }

  /**
   * Luo työjonon yksittäisen asiakkaan synkronointiin Lemonaidiin.
   * Päivittää asiakkaan tilintarkastajatiedot Lemonaidiin.
   */
  public synkronoiAsiakasLemonaidiinBatchiin(asiakasAvain: string, batch: FirestoreWriteBatch) {
    const tyoAvain = this.firestoreProvider.annaUusiAvain()
    const tyoUri = `tyojono/${asiakasAvain}/asiakas-lemontree-export/${tyoAvain}`
    const tyoData = {
      asiakasAvain: asiakasAvain,
      aloitettu: this.timestampService.now(),
    }
    batch.set(this.firestoreProvider.annaDoc(tyoUri), tyoData)
  }
}
